<template>
    <div class="max-w-[80%] rounded-lg border border-violet-200 bg-violet-50 shadow-sm" data-testid="role-play-setup-card">
        <div class="flex items-center gap-2 border-b border-violet-200 px-4 py-2">
            <svg class="h-4 w-4 text-violet-700" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-5.13a4 4 0 11-8 0 4 4 0 018 0zm6 0a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <h3 class="text-sm font-semibold text-violet-900">Set up your role-play</h3>
        </div>

        <div class="space-y-3 px-4 py-3">
            <label :for="`${fieldId}-name`" class="block text-xs">
                <span class="font-medium text-violet-900">Practicing with</span>
                <input
                    :id="`${fieldId}-name`"
                    v-model="form.persona_name"
                    type="text"
                    class="mt-1 w-full rounded border border-violet-200 bg-white px-2 py-1 text-sm focus:border-violet-500 focus:outline-none disabled:bg-slate-100"
                    placeholder="e.g. Sarah"
                    data-testid="role-play-setup-name"
                    :disabled="started" />
            </label>

            <div class="grid grid-cols-2 gap-2">
                <label :for="`${fieldId}-relationship`" class="block text-xs">
                    <span class="font-medium text-violet-900">Relationship</span>
                    <input
                        :id="`${fieldId}-relationship`"
                        v-model="form.persona_relationship"
                        type="text"
                        class="mt-1 w-full rounded border border-violet-200 bg-white px-2 py-1 text-sm focus:border-violet-500 focus:outline-none disabled:bg-slate-100"
                        placeholder="manager, peer, direct report…"
                        data-testid="role-play-setup-relationship"
                        :disabled="started" />
                </label>
                <label :for="`${fieldId}-personality`" class="block text-xs">
                    <span class="font-medium text-violet-900">Personality</span>
                    <input
                        :id="`${fieldId}-personality`"
                        v-model="form.persona_personality"
                        type="text"
                        class="mt-1 w-full rounded border border-violet-200 bg-white px-2 py-1 text-sm focus:border-violet-500 focus:outline-none disabled:bg-slate-100"
                        placeholder="direct, busy, conflict-averse…"
                        data-testid="role-play-setup-personality"
                        :disabled="started" />
                </label>
            </div>

            <label :for="`${fieldId}-situation`" class="block text-xs">
                <span class="font-medium text-violet-900">What you want to rehearse</span>
                <textarea
                    :id="`${fieldId}-situation`"
                    v-model="form.situation"
                    rows="3"
                    class="mt-1 w-full resize-none rounded border border-violet-200 bg-white px-2 py-1 text-sm focus:border-violet-500 focus:outline-none disabled:bg-slate-100"
                    placeholder="The opening 30 seconds of pushing back on scope…"
                    data-testid="role-play-setup-situation"
                    :disabled="started"></textarea>
            </label>

            <div class="flex items-center justify-between gap-2 pt-1">
                <span class="text-[11px] text-violet-700">
                    <span v-if="form.connection_id">Linked to an existing connection</span>
                    <span v-else>A new persona will be created</span>
                </span>
                <button
                    type="button"
                    class="rounded bg-violet-700 px-3 py-1 text-xs font-medium text-white hover:bg-violet-800 disabled:opacity-50"
                    data-testid="role-play-setup-start"
                    :disabled="started || !canStart"
                    @click="onStart">
                    {{ started ? "Starting…" : "Start role-play →" }}
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, useId } from "vue";

import type { RolePlaySetupResult } from "../types";

const props = defineProps<{ result: RolePlaySetupResult; toolName: string }>();

const emit = defineEmits<{
    // The parent submits the resulting message into the conversation. We
    // emit a structured payload; the parent decides whether to show it as
    // a real user bubble or as a synthetic confirmation.
    (e: "submit", payload: { message: string }): void;
}>();

// Vue 3 useId() — gives each field a deterministic id under SSR but unique
// across multiple cards rendered in the same conversation.
const fieldId = useId();

const form = reactive({
    persona_name: props.result.persona_name,
    persona_relationship: props.result.persona_relationship ?? "",
    persona_personality: props.result.persona_personality ?? "",
    situation: props.result.situation,
    connection_id: props.result.connection_id,
});
const started = ref(false);

const canStart = computed(() => Boolean(form.persona_name.trim()) && Boolean(form.situation.trim()));

const onStart = () => {
    if (!canStart.value || started.value) {
        return;
    }
    started.value = true;
    // Compose a structured user message that the agent can map directly to
    // a `start_role_play` call. The card has done the gathering; the agent
    // just confirms the values and dispatches.
    const parts: string[] = [`Yes — let's start the role-play.`, `Persona: ${form.persona_name.trim()}`];
    if (form.persona_relationship.trim()) {
        parts.push(`(${form.persona_relationship.trim()})`);
    }
    if (form.persona_personality.trim()) {
        parts.push(`Personality: ${form.persona_personality.trim()}.`);
    }
    parts.push(`Situation: ${form.situation.trim()}`);
    if (form.connection_id) {
        parts.push(`Use connection_id: ${form.connection_id}.`);
    }
    emit("submit", { message: parts.join(" ") });
};
</script>
